import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle, Users, Heart, Wifi, Star } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section, { SectionHeading } from "@/components/ui/Section";
import Card from "@/components/ui/Card";
import CTAButton from "@/components/ui/CTAButton";
import Field, { FormError } from "@/components/ui/Field";
import { API } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1768725845482-5cead8446b42?w=1600&q=85";

const testimonials = [
  { text: "Gracias a la formación de la Delegación pude empezar a compartir mi fe en Instagram. Ha sido un regalo.", name: "María, catequista", parish: "Parroquia Ntra. Sra. de la Cinta" },
  { text: "No sabía que ser misionero digital era tan sencillo. Ahora cada día comparto algo del Evangelio en mis redes.", name: "Carlos, joven universitario", parish: "Parroquia San Pedro" },
];

const VENTAJAS = [
  "Formación continua y acompañamiento",
  "Red de misioneros de toda la Diócesis",
  "Recursos y plantillas para evangelizar",
  "Comunidad de apoyo y oración",
];

const RASGOS = [
  { icon: Wifi, title: "Evangeliza online", desc: "Comparte fe en Instagram, TikTok, Facebook y WhatsApp" },
  { icon: Users, title: "En comunidad", desc: "Formas parte de una red de misioneros diocesanos" },
  { icon: Heart, title: "Con autenticidad", desc: "Evangeliza desde tu experiencia personal de fe" },
  { icon: Star, title: "Con formación", desc: "Te formamos y acompañamos en cada paso" },
];

export default function MisioneroDigital() {
  const [form, setForm] = useState({ name: "", age: "", parish: "", social_networks: "", motivation: "", email: "", phone: "", honeypot: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.motivation) {
      setError("Por favor, completa al menos tu nombre y tu motivación.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/missionary`, form, { withCredentials: true });
      setSuccess(true);
    } catch {
      setError("Ha ocurrido un error. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="misionero-page">
      <SEO
        title="Hazte Misionero Digital"
        description="Únete a la red de misioneros digitales de la Diócesis de Huelva: formación, recursos y comunidad para evangelizar desde tus redes sociales."
      />

      {/* Cuarto de los héroes que se dibujaban a mano; ahora usa PageHero. */}
      <PageHero
        content={{
          subtitle: "Únete a la Misión",
          title: "Misioneros Digitales",
          description: "Evangelizadores que, desde su smartphone y sus redes, llevan el amor de Cristo al mundo digital cada día.",
          bg_image: HERO_BG,
          overlay_opacity: 0.65,
          cta_primary_text: "Quiero apuntarme",
          cta_primary_url: "#formulario",
        }}
      />

      {/* Qué es un misionero digital */}
      <Section tone="light">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <Reveal distance={28}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-3">¿Qué es?</p>
            <h2 className="font-heading font-bold mb-6 text-balance" style={{ fontSize: "var(--text-h2)" }}>
              ¿Qué es un Misionero Digital?
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Un <strong className="text-foreground font-semibold">Misionero Digital</strong> es un cristiano
              comprometido que evangeliza en el entorno digital: comparte contenido de fe en sus redes,
              comenta con caridad, responde preguntas sobre la Iglesia y es testimonio auténtico de Cristo
              en Internet.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              No hace falta ser experto en tecnología. Hace falta amor a Jesucristo y ganas de compartirlo.
              La Delegación te da formación, herramientas y comunidad.
            </p>
            <ul className="space-y-3">
              {VENTAJAS.map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <div className="grid grid-cols-2 gap-4">
            {RASGOS.map((card, i) => (
              <Reveal key={i} delay={i * 70} distance={24}>
                <Card className="p-5 h-full" interactive>
                  <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center mb-3">
                    <card.icon className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <h3 className="font-heading font-bold text-sm mb-1">{card.title}</h3>
                  <p className="text-xs text-muted-foreground">{card.desc}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* Testimonios */}
      <Section tone="muted" width="reading" space="tight">
        <Reveal>
          <h2 className="font-heading text-2xl font-bold text-center mb-10">Testimonios de Misioneros</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 90} distance={24}>
              <Card className="p-6 h-full">
                <blockquote className="font-spiritual italic text-base mb-4 leading-relaxed">
                  «{t.text}»
                </blockquote>
                <p className="font-heading font-semibold text-sm">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.parish}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Formulario */}
      <Section tone="light" width="narrow" id="formulario" innerClassName="max-w-2xl">
        <Reveal>
          <SectionHeading
            label="El primer paso"
            title="Quiero ser Misionero Digital"
            description="Rellena el formulario y nos pondremos en contacto contigo en breve."
            align="center"
          />
        </Reveal>

        {success ? (
          <div data-testid="missionary-success-message" className="text-center py-16" role="status">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-primary-foreground" aria-hidden="true" />
            </div>
            <h3 className="font-heading text-2xl font-bold mb-3">¡Gracias por tu vocación misionera!</h3>
            <p className="text-muted-foreground mb-6">
              Hemos recibido tu solicitud. Nos pondremos en contacto contigo pronto para darte la
              bienvenida a la comunidad de misioneros digitales de la Diócesis de Huelva.
            </p>
            <p className="font-spiritual italic text-primary text-lg">
              «Id y haced discípulos a todas las naciones»
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="missionary-form"
            className="bg-card rounded-3xl border border-border p-8 shadow-rest space-y-5"
            noValidate
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Nombre completo"
                required
                data-testid="missionary-input-name"
                type="text"
                name="name"
                autoComplete="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Tu nombre"
              />
              <Field
                label="Edad"
                data-testid="missionary-input-age"
                type="text"
                inputMode="numeric"
                name="age"
                value={form.age}
                onChange={handleChange}
                placeholder="Tu edad"
              />
            </div>

            <Field
              label="Parroquia"
              data-testid="missionary-input-parish"
              type="text"
              name="parish"
              value={form.parish}
              onChange={handleChange}
              placeholder="Tu parroquia o comunidad"
            />

            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Email"
                data-testid="missionary-input-email"
                type="email"
                name="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                placeholder="tu@email.com"
              />
              <Field
                label="Teléfono"
                data-testid="missionary-input-phone"
                type="tel"
                name="phone"
                autoComplete="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="Teléfono de contacto"
              />
            </div>

            <Field
              label="Redes sociales que usas"
              data-testid="missionary-input-social"
              type="text"
              name="social_networks"
              value={form.social_networks}
              onChange={handleChange}
              placeholder="Ej: Instagram @tuusuario, TikTok…"
            />

            <Field
              label="¿Por qué quieres ser misionero digital?"
              required
              as="textarea"
              data-testid="missionary-input-motivation"
              name="motivation"
              rows={4}
              value={form.motivation}
              onChange={handleChange}
              placeholder="Cuéntanos tu motivación, tu historia de fe, por qué quieres evangelizar en el mundo digital…"
            />

            {/* Señuelo anti-spam: oculto también para tecnología asistiva. */}
            <input
              type="text"
              name="honeypot"
              value={form.honeypot}
              onChange={handleChange}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <FormError data-testid="missionary-error">{error}</FormError>

            <CTAButton
              type="submit"
              data-testid="missionary-submit-btn"
              disabled={loading}
              className="w-full py-3.5 font-bold"
            >
              {loading ? (
                "Enviando..."
              ) : (
                <>
                  <span>Quiero ser Misionero Digital</span>
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </>
              )}
            </CTAButton>
          </form>
        )}
      </Section>
    </div>
  );
}
